import React, { useEffect, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Card,
  TableRow,
  Paper,
  IconButton,
  Autocomplete,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import Grid from "@mui/material/Grid2"
import SingleSelect from "@/components/Select/SingleSelect"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import BillingDetails from "./BillingDetails"
import SecondaryButton from "../Buttons/SecondaryButton"
import useProductableAdjustments from "@/hooks/useProductableAdjustments"
import { z } from "zod"
import CustomAutoComplete from "../Select/CustomAutoComplete"

export const ProductableSchema = z.object({
  id: z.number(),
  name: z.string().min(3),
  code: z.string().min(1),
  quantity: z.number(),
  price: z.number(),
  type: z.number(),
  reason: z.number().default(0),
})

const ProductableAdjustments = ({
  controller,
  control,
  errors,
  onClose,
  name,
  helperText,
  label = "Products",
  isSubmitting,
  defaultProducts = [],
}) => {
  const Controller = controller

  const [productSearchQuery, setProductSearchQuery] = useState("")

  const {
    products,
    items: adjustedItems,
    isLoading: productLoading,
    handleAddedItem,
    handleRemoveItem,
    handleReset,
    handleTypeChange,
    handleReasonChange,
    handleQuantityChange,
    handlePriceChange,
  } = useProductableAdjustments(defaultProducts, productSearchQuery)
  const {
    data: enumResponse,
    isLoading: enumLoading,
    isError,
    isFetching,
  } = useCustomQuery(["stock-enums"], "stock-enums")
  return (
    <div>
      <Typography
        id="modal-modal-title"
        variant="h6"
        sx={{
          fontWeight: "500",
          fontSize: { xs: "12px", md: "16px" },
          marginBottom: 2,
          marginTop: 4,
        }}
        className="text-black"
      >
        {label}
      </Typography>
      <Grid container spacing={2} sx={{ marginTop: 4 }}>
        <Grid size={12}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              borderRadius: "7px",
            }}
            className="rmui-table border"
          >
            <Table aria-label="table">
              <TableHead className="bg-f6f7f9">
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "30px",
                    }}
                    className="text-black border-bottom"
                  >
                    S.N.
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "150px",
                    }}
                    className="text-black border-bottom"
                  >
                    Item
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "100px",
                    }}
                    className="text-black border-bottom"
                  >
                    Quantity
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "100px",
                    }}
                    className="text-black border-bottom"
                  >
                    Price
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "100px",
                    }}
                    className="text-black border-bottom"
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "100px",
                    }}
                    className="text-black border-bottom"
                  >
                    Reason
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 24px",
                      fontSize: "14px",
                      width: "30px",
                    }}
                    className="text-black border-bottom"
                  >
                    Remove
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <Controller
                  name={name}
                  control={control}
                  defaultValue={[]}
                  render={({
                    field: { onChange: fieldOnChange, value: fieldValue },
                    fieldState: { error, isTouched, isDirty },
                  }) => (
                    <>
                      {useEffect(() => {
                        fieldOnChange(adjustedItems)
                      }, [adjustedItems])}
                      {adjustedItems.map((value, index) => {
                        return (
                          <TableRow key={value.id}>
                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "30px",
                              }}
                              className="border-bottom"
                            >
                              {index + 1}
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "150px",
                              }}
                              className="border-bottom"
                            >
                              <Typography
                                variant="button"
                                gutterBottom
                                sx={{ display: "block" }}
                              >
                                {console.log("value", value)}
                                {value.sku}
                              </Typography>
                              <Typography
                                variant="caption"
                                gutterBottom
                                sx={{ display: "block" }}
                              >
                                {value.code}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "100px",
                              }}
                              className="border-bottom"
                            >
                              <TextField
                                size="small"
                                sx={{
                                  width: "100px",
                                }}
                                value={value.quantity}
                                onChange={(event) =>
                                  handleQuantityChange(
                                    index,
                                    event.target.value
                                  )
                                }
                                type="number"
                                slotProps={{
                                  htmlInput: { min: 1 },
                                }}
                              />
                              <Typography
                                variant="caption"
                                gutterBottom
                                sx={{
                                  display: "block",
                                  color:
                                    value.stock == 0
                                      ? "red"
                                      : value.stock < 10
                                        ? "orange"
                                        : "grey",
                                }}
                              >
                                Available: {value.stock}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "100px",
                              }}
                              className="border-bottom"
                            >
                              <TextField
                                size="small"
                                sx={{
                                  width: "100px",
                                }}
                                value={value.price}
                                onChange={(event) =>
                                  handlePriceChange(index, event.target.value)
                                }
                                type="number"
                                slotProps={{
                                  htmlInput: { min: 1, step: 0.01 },
                                }}
                              />
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "60px",
                              }}
                              className="border-bottom"
                            >
                              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                <Controller
                                  name="type"
                                  control={control}
                                  render={({ field }) => (
                                    <Autocomplete
                                      {...field}
                                      options={enumResponse?.data?.type || []}
                                      getOptionLabel={(option) =>
                                        option.name || ""
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={value.type}
                                      loading={enumLoading}
                                      onChange={(event, newValue) => {
                                        field.onChange(newValue) // Update form value
                                        handleTypeChange(index, newValue) // Custom handler
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Type"
                                          error={!!errors.type}
                                          helperText={errors.type?.message}
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </FormControl>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "140px",
                              }}
                              className="border-bottom"
                            >
                              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                <Controller
                                  name="reason"
                                  control={control}
                                  render={({ field }) => (
                                    <Autocomplete
                                      {...field}
                                      options={enumResponse?.data?.reason || []}
                                      getOptionLabel={(option) =>
                                        option.name || ""
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={value.reason}
                                      loading={enumLoading}
                                      onChange={(event, newValue) => {
                                        field.onChange(newValue)
                                        handleReasonChange(index, newValue)
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Reason"
                                          error={!!errors.reason}
                                          helperText={errors.reason?.message}
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </FormControl>
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "15px 20px",
                                fontSize: "14px",
                                width: "30px",
                              }}
                              className="border-bottom"
                            >
                              <IconButton
                                aria-label="remove"
                                onClick={() => handleRemoveItem(value)}
                              >
                                <ClearIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </>
                  )}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ marginTop: 4 }}>
        <Grid size={6}>
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <CustomAutoComplete
              label="Product"
              loading={productLoading}
              options={products}
              error={!!errors[name]}
              helperText={errors[name]?.message}
              optionLabel="sku"
              optionValue="id"
              onChange={handleAddedItem}
              onInputChange={(searchText, value, type) => {
                if (type == "input") {
                  setProductSearchQuery(searchText)
                }
              }}
            />
          </FormControl>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={6}>
            <SecondaryButton
              fullWidth
              label="Reset"
              onClick={handleReset}
              color="secondary"
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}
export default ProductableAdjustments
